import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ReportRequest} from '../../../kitchen/shared/domain/report/report-request.model';
import {ReportRunnerService} from './report-runner.service';
import {SnackBarService} from '../../services/snackbar.service';
import {SummedMasterReport} from '../../../kitchen/shared/domain/report/summed-master-report.model';
import {CmTableColumn} from '../table/cm-table-column.model';
import {
  PromoReportMetricsDetailsMap,
  ReportMetric,
  ReportMetricDetails,
  ReportMetricsDetailsMap
} from '../../../kitchen/shared/domain/report/report-metric.enum';
import {CmTableColumnCommonService} from '../table/cm-table-column-common.service';
import {CsvExportUtils} from '../../utils/csv-export-utils';
import {DateRangeTypeLabels} from '../../domain/date-range-type.enum';
import {ReportMetricUtils} from '../../../kitchen/shared/domain/report/report-metric-utils.const';
import {ServerErrorUtils} from '../../utils/server-error-utils';
import {PromocastReport} from '../../../kitchen/shared/domain/report/promocast-report.model';
import {Principal} from '../../auth/principal.service';
import {ReportType} from '../../../kitchen/shared/domain/report/report-type.enum';

@Component({
  selector: 'cm-report-runner',
  templateUrl: './report-runner.component.html',
  styleUrls: ['./report-runner.component.scss'],
  host: {'class': 'cm-report-runner'}
})

export class ReportRunnerComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  @Input()
  usePublisherReport: boolean;

  @Output()
  backButtonOnClick = new EventEmitter<VoidFunction>();

  @Output()
  reportRunning = new EventEmitter<VoidFunction>();

  @Output()
  reportRunningFinished = new EventEmitter<VoidFunction>();

  dataSource;

  allColumns: CmTableColumn[] = [];
  requestedColumns: CmTableColumn[] = [];
  visibleColumns: string[] = [];

  config: ReportRequest;
  DateRangeTypeLabels = DateRangeTypeLabels;
  running = false;
  private reportMetricsDetailsMap: { [key: string]: ReportMetricDetails } = ReportMetricsDetailsMap();
  private promoReportMetricsDetailsMap: { [key: string]: ReportMetricDetails } = PromoReportMetricsDetailsMap();

  private total: SummedMasterReport;
  protected requestType: String = 'sync';

  filter = {
    name: ''
  };

  constructor(private service: ReportRunnerService,
              private commonColumns: CmTableColumnCommonService,
              private sbs: SnackBarService,
              public principal: Principal) {

  }

  ngOnInit(): void {

    if (this.principal != null && this.principal.publisher != null && this.principal.publisher.promocastEnrolled !== true) {
      this.dataSource = new MatTableDataSource<SummedMasterReport>();
    } else {
      this.dataSource = new MatTableDataSource<PromocastReport>();
    }

    const t = this.commonColumns;
    const metricColumns = this.commonColumns.getAllForReportMetrics();
    this.allColumns = this.allColumns.concat(metricColumns.filter(m => !this.allColumns.map(c => c.key).includes(m.key)));
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    const customFilterFn = (data: SummedMasterReport, filter: string) => {
      if (this.filter.name) {
        const visibleColumns = this.allColumns.filter(c => c.visible);
        return visibleColumns.some(c => data[c.key] && typeof data[c.key] === 'string' && data[c.key].toLowerCase().includes(this.filter.name.toLowerCase()));
      }
      return true;
    };
    this.dataSource.filterPredicate = customFilterFn;
  }

  public runPromoReport(config: ReportRequest) {
    this.config = config;
    const requestedMetricFields = config.metrics.map(m => this.promoReportMetricsDetailsMap[m].field);
    this.requestedColumns = this.allColumns.filter(c => requestedMetricFields.includes(c.key));
    this.config.reportType = ReportType.PROMOCAST;
    // set footer cell resolution
    const numericMetricsFields = ReportMetricUtils.getNumericMetricsFields();
    this.requestedColumns.forEach((c, i) => {
      c.footerCell = (row: SummedMasterReport) => {
        if (i === 0) {
          return 'TOTAL';
        }
        return this.total && numericMetricsFields.includes(c.key) ? c.cell(this.total) : '';
      };
    });

    // set visible columns to be requested ones
    this.visibleColumns = this.requestedColumns.map(c => c.key);

    this.running = true;
    this.reportRunning.emit();
    const reportObservable = this.service.publisherPromoReport(config);
    reportObservable.subscribe(next => {
      this.dataSource.data = next.report || [];
      this.total = next.total;
      this.running = false;
      this.reportRunningFinished.emit();
    }, error => {
      this.running = false;
      this.reportRunningFinished.emit();
      ServerErrorUtils.serverValidationOrMsg(error, this.sbs, 'Unexpected error occurred, please try again');
    });

  }

  public runReport(config: ReportRequest) {
    this.config = config;
    const requestedMetricFields = config.metrics.map(m => this.reportMetricsDetailsMap[m].field);
    this.requestedColumns = this.allColumns.filter(c => requestedMetricFields.includes(c.key));

    // order columns
    this.requestedColumns.sort((a, b) => {
      if (
        a.key === this.reportMetricsDetailsMap[ReportMetric.AD_UNIT].field ||
        a.key === this.reportMetricsDetailsMap[ReportMetric.DAY].field ||
        a.key === this.reportMetricsDetailsMap[ReportMetric.SITE].field ||
        a.key === this.reportMetricsDetailsMap[ReportMetric.PUBLISHER].field ||
        a.key === this.reportMetricsDetailsMap[ReportMetric.DEMAND_PARTNER].field ||
        a.key === this.reportMetricsDetailsMap[ReportMetric.WIDGET].field) {
        return -1;
      }
      if (
        b.key === this.reportMetricsDetailsMap[ReportMetric.AD_UNIT].field ||
        b.key === this.reportMetricsDetailsMap[ReportMetric.SITE].field ||
        b.key === this.reportMetricsDetailsMap[ReportMetric.PUBLISHER].field ||
        b.key === this.reportMetricsDetailsMap[ReportMetric.DEMAND_PARTNER].field ||
        b.key === this.reportMetricsDetailsMap[ReportMetric.WIDGET].field ||
        b.key === this.reportMetricsDetailsMap[ReportMetric.DAY].field) {
        return 1;
      }
      if (a.header < b.header) {
        return -1;
      }
      if (a.header > b.header) {
        return 1;
      }
      return 0;
    });

    // check if all fields have corresponding col def, if not add default
    const fieldsWithNoColDef = requestedMetricFields.filter(field => !this.allColumns.find(c => c.key === field));
    fieldsWithNoColDef.forEach(field => {
      this.allColumns.push(<CmTableColumn>{
        key: field,
        header: field,
        cell: (row: SummedMasterReport) => row[field]
      });
    });

    // set footer cell resolution
    const numericMetricsFields = ReportMetricUtils.getNumericMetricsFields();
    this.requestedColumns.forEach((c, i) => {
      c.footerCell = (row: SummedMasterReport) => {
        if (i === 0) {
          return 'TOTAL';
        }
        return this.total && numericMetricsFields.includes(c.key) ? c.cell(this.total) : '';
      };
    });

    // set visible columns to be requested ones
    this.visibleColumns = this.requestedColumns.map(c => c.key);

    this.running = true;
    this.reportRunning.emit();
    const reportObservable = this.usePublisherReport ? this.service.publisherReport(config) : this.service.kitchenReport(config);
    reportObservable.subscribe(next => {
      this.dataSource.data = next.report || [];
      this.total = next.total;
      this.running = false;
      this.reportRunningFinished.emit();
      this.requestType = next.request_type;
      if (this.requestType === 'async') {
        this.sbs.success('The report will be emailed to you upon completion', 3000);
      }
    }, error => {
      this.running = false;
      this.reportRunningFinished.emit();
      ServerErrorUtils.serverValidationOrMsg(error, this.sbs, 'Unexpected error occurred, please try again');
    });

  }

  applyFilter() {
    this.dataSource.filter = <string>(this.filter ? this.filter : '');
  }

  exportCsv() {
    const dataToIncludeInCsv = [];
    this.dataSource.data.forEach(rowData => {
      const csvRow = this.requestedColumns.reduce((prev, curr) => {
        prev[curr.header] = rowData[curr.key];
        return prev;
      }, {});
      csvRow['NC'] = rowData.bidderCalc ? 'NC' : '';
      dataToIncludeInCsv.push(csvRow);
    });
    CsvExportUtils.exportCsv(dataToIncludeInCsv, 'report-export');
    return dataToIncludeInCsv;
  }

  protected readonly JSON = JSON;
}
